import { arbitrum, arbitrumSepolia, base, baseSepolia, celoMainnet } from "@account-kit/infra";
import {
  morphHolesky,
  kakarotSepolia,
  scrollSepolia,
  inkSepolia,
  liskSepolia,
  celoAlfajores as celoAlfajoresChain,
  hederaTestnet as hederaTestnetChain,
  unichainSepolia as unichainSepoliaChain,
  optimismSepolia,
  bsc,
  lisk,
  mantleSepoliaTestnet,
  Chain,
  mantle,
} from "viem/chains";
import { IS_MAINNET } from "@shared/src/constants";
import { getNetworkIconUrl } from "@sdk/utils/config";

export type IxChain = Chain & { iconUrl: string };

export const scrollSep: IxChain & { iconUrl: string } = {
  ...scrollSepolia,
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/scroll.svg" : "",
};

//mainnet chains with fallback
export const baseWithFallback: Chain = {
  ...base,
  rpcUrls: {
    ...base.rpcUrls,
    default: {
      http: [process.env.API_BASE_URL + "/proxy/rpc/base-mainnet"],
    },
    fallback: {
      http: ["https://base-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
  },
};

export const bscWithFallback: Chain = {
  ...bsc,
  rpcUrls: {
    ...bsc.rpcUrls,
    default: {
      http: ["https:/"],
    },
    fallback: {
      http: ["https://bnb-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
  },
};

export const mantleWithFallback: Chain = {
  ...mantle,
  rpcUrls: {
    ...mantle.rpcUrls,
    fallback: {
      http: ["https://mantle-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
  },
};

export const liskWithFallback: Chain = {
  ...lisk,
  rpcUrls: {
    ...lisk.rpcUrls,
    fallback: {
      http: ["https://lisk.drpc.org"],
    },
  },
};

export const arbWithFallback: Chain = {
  ...arbitrum,
  rpcUrls: {
    ...arbitrum.rpcUrls,
    fallback: {
      http: ["https://arb-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
  },
};

export const celoWithFallback: Chain = {
  ...celoMainnet,
  rpcUrls: {
    ...celoMainnet.rpcUrls,
    default: {
      http: ["https://celo-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
    fallback: {
      http: ["https://celo-mainnet.g.alchemy.com/v2/" + process.env.ALCHEMY_API_KEY],
    },
  },
};

// testnet chains
export const mHolesky = {
  ...morphHolesky,
  rpcUrls: {
    default: {
      http: ["https://rpc-holesky.morphl2.io"],
    },
  },
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/morph.jpeg" : "",
} as IxChain;
export const kSepolia = {
  ...kakarotSepolia,
  id: 920637907288165,
  rpcUrls: {
    default: {
      http: ["https://sepolia-rpc.kakarot.org"],
    },
  },
  blockExplorers: {
    default: {
      name: "BlockScout",
      url: " https://blockscout-kkrt-sepolia.karnot.xyz/",
    },
  },
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/kakarot.png" : "",
} satisfies IxChain;

export const neoX = {
  id: 12227332,
  name: "Neo X",
  network: "neox-testnet",
  nativeCurrency: {
    decimals: 18,
    name: "Gas",
    symbol: "GAS",
  },
  rpcUrls: {
    default: {
      http: ["https://neoxt4seed1.ngd.network"],
    },
  },
  blockExplorers: {
    default: {
      name: "xt44scan",
      url: "https://xt4scan.ngd.network/",
    },
  },
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/neox.avif" : "",
} as IxChain;

export const inkSep = {
  ...inkSepolia,
  iconUrl: getNetworkIconUrl(inkSepolia.name),
};

export const arbSepolia = {
  ...arbitrumSepolia,
  iconUrl: getNetworkIconUrl(arbitrumSepolia.name),
};

export const baseSep = {
  ...baseSepolia,
  iconUrl: getNetworkIconUrl(baseSepolia.name),
  rpcUrls: {
    default: {
      http: [process.env.API_BASE_URL + "/proxy/rpc/base-sepolia"],
    },
  },
};

export const liskSep = {
  ...liskSepolia,
  iconUrl: getNetworkIconUrl(liskSepolia.name),
};

export const celoAlfajores: IxChain = {
  ...celoAlfajoresChain,
  name: "Celo Alfajores",
  rpcUrls: {
    default: {
      http: ["https://celo-alfajores.g.alchemy.com/v2/hntMXgOHf-CwMij-j6_pxYHble56eXxm"],
    },
  },
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/celo.png" : "",
};

export const unichainSepolia = {
  ...unichainSepoliaChain,
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/unichain.png" : "",
} as IxChain;

export const hederaTestnet: IxChain = {
  ...hederaTestnetChain,
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/hedera.png" : "",
};

export const mantleTestnet: IxChain = {
  ...mantleSepoliaTestnet,
  iconUrl: typeof global?.window !== "undefined" ? window.location.origin + "/images/network-logos/mantle.png" : "",
};

export const optimismSep: IxChain = {
  ...optimismSepolia,
  iconUrl: getNetworkIconUrl("optimism"),
};

//mainnet chains
export const mainnetChains = [
  baseWithFallback,
  celoWithFallback,
  arbWithFallback,
  bscWithFallback,
  liskWithFallback,
].map(c => ({
  ...c,
  iconUrl: getNetworkIconUrl(c.name),
  rpcUrls: { ...c.rpcUrls, alchemy: { http: [c.rpcUrls.default.http[0]] } },
}));

export const mainnetChainsSponsored = [baseWithFallback, arbWithFallback, celoWithFallback];

export const testnetChainsSponsored = [arbSepolia, baseSepolia];

//testnet chains
export const testnetChains = [
  baseSep,
  arbSepolia, // primary chain
  celoAlfajores,
  mHolesky,
  liskSep,
  scrollSep,
  unichainSepolia,
  hederaTestnet,
  mantleTestnet,
  optimismSep,
].map(c => ({
  ...c,
  rpcUrls: { ...c.rpcUrls, alchemy: { http: [c.rpcUrls.default.http[0]] } },
}));

export const chains: IxChain[] = IS_MAINNET ? mainnetChains : testnetChains;
export const primaryChain = chains[0];

export default chains;
